<script setup>
// NOTICE: KAPTION-INTEGRATION
// WhatsApp Preview component using wapreview.kaptionai.com iframe
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import themeEventEmitter from 'shared/helpers/themeEventEmitter';
import { useTemplateRenderer } from 'dashboard/composables/useTemplateRenderer';

const props = defineProps({
  // The full template object from WhatsApp
  template: {
    type: Object,
    default: null,
  },
  // Rendered body text (with variables replaced)
  renderedBody: {
    type: String,
    default: '',
  },
  // Loading state
  loading: {
    type: Boolean,
    default: false,
  },
  // Show bare mode (message only) or full phone frame
  bare: {
    type: Boolean,
    default: true,
  },
  // Company name to show in phone header
  companyName: {
    type: String,
    default: 'Business',
  },
  // CSS variable name for gradient blending (e.g., '--solid-1', '--slate-2')
  // This should match the container's background CSS variable
  gradientVar: {
    type: String,
    default: '--solid-1',
  },
});

const PREVIEW_URL = 'https://wapreview.kaptionai.com/render';

const { formatWhatsAppText } = useTemplateRenderer();

const iframeRef = ref(null);
const iframeLoaded = ref(false);
const isScrolledToBottom = ref(false);

// Detect current theme
const isDarkMode = ref(document.body.classList.contains('dark'));

// Show gradient when we have a template, not loading, and not scrolled to bottom
const showGradient = computed(
  () => props.template && !props.loading && !isScrolledToBottom.value
);

// Listen for scroll messages from the iframe
const handleIframeMessage = event => {
  // Only accept messages from our preview URL
  if (!event.origin.includes('wapreview.kaptionai.com')) return;

  if (event.data?.type === 'SCROLL_STATE') {
    isScrolledToBottom.value = event.data.isAtBottom === true;
  }
};

// Compute the gradient style using the CSS variable directly
// CSS variables like --solid-1 contain space-separated RGB values (e.g., "23 23 26")
// We use the modern CSS color syntax: rgb(var(--solid-1) / alpha)
const gradientStyle = computed(() => {
  const cssVar = props.gradientVar;
  return {
    background: `linear-gradient(to top, rgb(var(${cssVar})) 0%, rgb(var(${cssVar}) / 0.95) 15%, rgb(var(${cssVar}) / 0.7) 40%, rgb(var(${cssVar}) / 0) 100%)`,
  };
});

// Send template data to iframe via postMessage
const sendTemplateToIframe = () => {
  if (!iframeRef.value || !iframeLoaded.value) return;

  const iframe = iframeRef.value;

  if (props.loading) {
    iframe.contentWindow?.postMessage(
      {
        type: 'LOADING',
        theme: isDarkMode.value ? 'dark' : 'light',
      },
      '*'
    );
    return;
  }

  // eslint-disable-next-line no-use-before-define
  if (templatePayload.value) {
    iframe.contentWindow?.postMessage(
      {
        type: 'UPDATE_TEMPLATE',
        // eslint-disable-next-line no-use-before-define
        payload: templatePayload.value,
        theme: isDarkMode.value ? 'dark' : 'light',
      },
      '*'
    );
  }
};

// Listen for theme changes
const handleThemeChange = event => {
  isDarkMode.value = event.theme === 'dark';
  sendTemplateToIframe();
};

let unsubscribeTheme = null;

onMounted(() => {
  unsubscribeTheme = themeEventEmitter.onThemeChange(handleThemeChange);
  // Listen for scroll messages from iframe
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  if (unsubscribeTheme) {
    unsubscribeTheme();
  }
  window.removeEventListener('message', handleIframeMessage);
});

// Build the template object for the iframe
const templatePayload = computed(() => {
  if (!props.template) return null;

  const components = [];

  // Process each component from the template
  props.template.components?.forEach(component => {
    if (component.type === 'HEADER') {
      components.push({
        type: 'HEADER',
        format: component.format || 'TEXT',
        text: component.text || '',
        // Handle media headers
        ...(component.format === 'IMAGE' && { example: component.example }),
        ...(component.format === 'VIDEO' && { example: component.example }),
        ...(component.format === 'DOCUMENT' && { example: component.example }),
        ...(component.format === 'LOCATION' && {
          location: component.location,
        }),
      });
    } else if (component.type === 'BODY') {
      // Use rendered body if available, otherwise use original
      // Apply WhatsApp formatting (bold, italic, etc.)
      const bodyText = props.renderedBody || component.text || '';
      components.push({
        type: 'BODY',
        text: bodyText,
        // Send formatted HTML version for rich display
        formattedText: formatWhatsAppText(bodyText),
      });
    } else if (component.type === 'FOOTER') {
      components.push({
        type: 'FOOTER',
        text: component.text || '',
      });
    } else if (component.type === 'BUTTONS') {
      components.push({
        type: 'BUTTONS',
        buttons:
          component.buttons?.map(btn => ({
            type: btn.type || 'QUICK_REPLY',
            text: btn.text || '',
            ...(btn.url && { url: btn.url }),
            ...(btn.phone_number && { phone_number: btn.phone_number }),
            ...(btn.example && { example: btn.example }),
          })) || [],
      });
    }
  });

  return {
    name: props.template.name || 'template',
    language: props.template.language || 'en',
    category: props.template.category || 'UTILITY',
    components,
  };
});

// Handle iframe load
const handleIframeLoad = () => {
  iframeLoaded.value = true;
  // Small delay to ensure iframe is ready to receive messages
  setTimeout(() => {
    sendTemplateToIframe();
  }, 100);
};

// Watch for changes and update iframe
watch(
  [() => props.template, () => props.renderedBody, () => props.loading],
  () => {
    sendTemplateToIframe();
  },
  { deep: true }
);

// Iframe URL with bare mode and initial theme
const iframeSrc = computed(() => {
  const params = new URLSearchParams({
    bare: props.bare ? 'true' : 'false',
    dark: isDarkMode.value ? 'true' : 'false',
  });
  // Add company name for phone frame mode
  if (!props.bare && props.companyName) {
    params.set('company_name', props.companyName);
  }
  return `${PREVIEW_URL}?${params.toString()}`;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="kaption-whatsapp-preview relative overflow-hidden rounded-xl"
    :class="[
      bare
        ? isDarkMode
          ? 'bg-[#0b141a]'
          : 'bg-[#efeae2]'
        : isDarkMode
          ? 'bg-[#0b141a]'
          : 'bg-[#e5ddd5]',
      { 'with-pattern': bare },
    ]"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading || !iframeLoaded"
      class="absolute inset-0 flex items-center justify-center z-10"
      :class="
        bare
          ? isDarkMode
            ? 'bg-[#0b141a]'
            : 'bg-[#efeae2]'
          : 'bg-n-alpha-black1'
      "
    >
      <div class="flex flex-col items-center gap-2">
        <div
          class="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          :class="isDarkMode ? 'border-[#00a884]' : 'border-[#25d366]'"
        />
        <span
          class="text-xs"
          :class="isDarkMode ? 'text-[#8696a0]' : 'text-[#667781]'"
        >
          Loading preview...
        </span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!template && !loading"
      class="flex items-center justify-center h-full min-h-[200px]"
    >
      <span
        class="text-sm"
        :class="isDarkMode ? 'text-[#8696a0]' : 'text-[#667781]'"
      >
        Select a template to preview
      </span>
    </div>

    <!-- iframe preview -->
    <iframe
      v-show="template && !loading"
      ref="iframeRef"
      :src="iframeSrc"
      class="w-full h-full min-h-[200px] border-0 rounded-xl"
      title="WhatsApp Template Preview"
      sandbox="allow-scripts allow-same-origin"
      @load="handleIframeLoad"
    />

    <!-- Bottom gradient overlay for smooth blending - only shown in bare mode -->
    <div
      v-if="showGradient"
      class="absolute bottom-0 left-0 right-0 h-24 pointer-events-none z-20"
      :style="gradientStyle"
    />
  </div>
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>

<style scoped>
/* WhatsApp-like chat background pattern - only for bare mode */
.kaption-whatsapp-preview.with-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.dark .kaption-whatsapp-preview.with-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
</style>
